import { useEffect, useRef, useState, useCallback } from 'react'
import mqtt from 'mqtt'

// --- Types ---

export type NodeType = 'enterprise' | 'site' | 'area' | 'line' | 'device' | 'property'

export interface GraphNode {
  id: string
  label: string
  type: NodeType
  value?: string
  lastSeen: number
  color: string
  size: number
  x?: number
  y?: number
}

export interface GraphLink {
  source: string
  target: string
  type: 'contains' | 'feeds-into' | 'monitors'
}

export interface GraphData {
  nodes: GraphNode[]
  links: GraphLink[]
}

export interface GraphStats {
  messageCount: number
  nodeCount: number
  linkCount: number
  messagesPerSecond: number
}

export interface KnowledgeGraphConfig {
  wsAddress: string
  username: string
  password: string
  topicFilter: string | string[]
  clientIdPrefix?: string
}

// --- Constants ---

const NODE_CONFIG: Record<NodeType, { color: string; size: number }> = {
  enterprise: { color: '#1e3a5f', size: 8 },
  site: { color: '#2563eb', size: 6 },
  area: { color: '#0d9488', size: 5 },
  line: { color: '#16a34a', size: 4 },
  device: { color: '#ea580c', size: 3 },
  property: { color: '#a855f7', size: 2 },
}

const DEPTH_TO_TYPE: NodeType[] = ['enterprise', 'site', 'area', 'line', 'device', 'property']

function getNodeType(depth: number): NodeType {
  if (depth >= DEPTH_TO_TYPE.length) {
    return 'property'
  }
  return DEPTH_TO_TYPE[depth]
}

// --- Hook ---

export function useKnowledgeGraph(config: KnowledgeGraphConfig) {
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] })
  const [isConnected, setIsConnected] = useState(false)
  const [stats, setStats] = useState<GraphStats>({
    messageCount: 0,
    nodeCount: 0,
    linkCount: 0,
    messagesPerSecond: 0,
  })

  const nodesMapRef = useRef<Map<string, GraphNode>>(new Map())
  const linksMapRef = useRef<Map<string, GraphLink>>(new Map())
  const clientRef = useRef<mqtt.MqttClient | null>(null)
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)
  const messageCountRef = useRef(0)
  const lastCountRef = useRef(0)
  const lastFlushedCountsRef = useRef({ nodes: 0, links: 0 })
  const mpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const processMessageRef = useRef<(topic: string, message: Buffer) => void>(() => {})

  const flush = useCallback(() => {
    if (!mountedRef.current) {
      return
    }

    const nodeCount = nodesMapRef.current.size
    const linkCount = linksMapRef.current.size
    const prev = lastFlushedCountsRef.current

    // Only push new graphData when the structure changed (new nodes/links)
    if (nodeCount !== prev.nodes || linkCount !== prev.links) {
      const nodes = Array.from(nodesMapRef.current.values())
      const links = Array.from(linksMapRef.current.values())
      setGraphData({ nodes, links })
      lastFlushedCountsRef.current = { nodes: nodeCount, links: linkCount }
    }

    setStats((s) => ({
      ...s,
      nodeCount,
      linkCount,
      messageCount: messageCountRef.current,
    }))
  }, [])

  const processMessage = useCallback((topic: string, message: Buffer) => {
    if (!mountedRef.current) {
      return
    }
    const segments = topic.split('/')
    let parentId = ''

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]
      const nodeId = parentId ? `${parentId}/${segment}` : segment
      const nodeType = getNodeType(i)
      const cfg = NODE_CONFIG[nodeType]

      const existing = nodesMapRef.current.get(nodeId)
      if (existing) {
        existing.lastSeen = Date.now()
        if (i === segments.length - 1) {
          existing.value = message.toString()
        }
      } else {
        if (nodesMapRef.current.size >= 5000) {
          break
        }
        nodesMapRef.current.set(nodeId, {
          id: nodeId,
          label: segment,
          type: nodeType,
          value: i === segments.length - 1 ? message.toString() : undefined,
          lastSeen: Date.now(),
          color: cfg.color,
          size: cfg.size,
        })
      }

      if (parentId) {
        const linkId = `${parentId}-->${nodeId}`
        if (!linksMapRef.current.has(linkId)) {
          linksMapRef.current.set(linkId, {
            source: parentId,
            target: nodeId,
            type: 'contains',
          })
        }
      }

      parentId = nodeId
    }

    messageCountRef.current++
  }, [])
  processMessageRef.current = processMessage

  useEffect(() => {
    mountedRef.current = true

    if (!config.wsAddress) {
      return
    }

    const prefix = config.clientIdPrefix || 'stories_kg'
    const suffix = Math.random().toString(16).slice(2, 10)
    const clientId = `${prefix}_${suffix}`

    const client = mqtt.connect(`wss://${config.wsAddress}:443/mqtt`, {
      protocol: 'wss',
      port: 443,
      keepalive: 20,
      clientId,
      username: config.username,
      password: config.password,
    })
    clientRef.current = client

    client.on('connect', () => {
      if (!mountedRef.current) {
        return
      }
      setIsConnected(true)
      const topics = Array.isArray(config.topicFilter) ? config.topicFilter : [config.topicFilter]
      client.subscribe(topics, { qos: 1 })
    })

    client.on('message', (topic, message) => {
      processMessageRef.current(topic, message)
    })

    client.on('close', () => {
      if (mountedRef.current) {
        setIsConnected(false)
      }
    })

    client.on('error', (err) => {
      console.error('Knowledge graph MQTT error:', err)
    })

    // Flush graph data every 2 seconds (fixed interval, not debounce)
    flushIntervalRef.current = setInterval(flush, 2000)

    // Messages per second counter
    mpsIntervalRef.current = setInterval(() => {
      if (!mountedRef.current) {
        return
      }
      const current = messageCountRef.current
      const mps = current - lastCountRef.current
      lastCountRef.current = current
      setStats((prev) => ({ ...prev, messagesPerSecond: mps }))
    }, 1000)

    return () => {
      mountedRef.current = false
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current)
      }
      if (mpsIntervalRef.current) {
        clearInterval(mpsIntervalRef.current)
      }
      client.removeAllListeners()
      client.end()
    }
  }, [
    config.wsAddress,
    config.username,
    config.password,
    Array.isArray(config.topicFilter) ? config.topicFilter.join('\n') : config.topicFilter,
    config.clientIdPrefix,
  ])

  return { graphData, isConnected, stats }
}
